import _ from "lodash";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import Logger from "utils/logger";
import CurrentSession from "data/userSession";
import { monitored } from "utils/queryMonitor";

const FETCHED = [];

const identifier = (c, i) => `${c} ${i}`;

// memoize caches the returned promise; if a fetch rejects (transient network
// on resume from background) the dead promise would stick under its key and
// every retry -- re-render, navigate back -- would re-await it, recovering only
// on app restart. Evict the key on rejection so the next call actually retries.
const evictOnReject = (memoized, keyFn) => {
  const wrapper = (...args) => {
    const promise = memoized(...args);
    promise.catch(() => memoized.cache.delete(keyFn(...args)));
    return promise;
  };
  // Keep the memoize cache reachable so resetCache()'s .cache.clear() still works.
  wrapper.cache = memoized.cache;
  return wrapper;
};

const FetchFromCache = evictOnReject(
  _.memoize(
    (collection, id) =>
      monitored(`FS ${identifier(collection, id)}`, async () => {
        Logger.debug(
          `[FS-DB] Fetching document from ${identifier(collection, id)}`
        );
        FETCHED.push(identifier(collection, id));

        const docRef = doc(db, collection, id);
        const response = await getDoc(docRef);
        Logger.debug(
          `[FS-DB] ${identifier(collection, id)} success: ${
            response.id ? true : false
          }`
        );
        return {
          id: response.id,
          data: response.data(),
        };
      }),
    identifier
  ),
  identifier
);

const QueryFromCache = evictOnReject(
  _.memoize(
    (collectionName, where, key) =>
      monitored(`FS ${identifier(collectionName, key)}`, async () => {
        Logger.debug(
          `[FS-DB] Querying from ${identifier(collectionName, key)}`
        );

        const q = await getDocs(
          query(collection(db, collectionName), ...where)
        );
        _.forEach(q.docs, (doc) => {
          FETCHED.push(identifier(collectionName, doc.id));
        });
        Logger.debug(
          `[FS-DB] ${identifier(collectionName, key)} returned ${
            q.docs.length
          } docs`
        );
        return q.docs;
      }),
    (c, w, key) => key
  ),
  (c, w, key) => key
);

const PersistentQueryFromCache = evictOnReject(
  _.memoize(
    (collectionName, where, key) =>
      monitored(
        `FS ${identifier(collectionName, key)} (persistent)`,
        async () => {
          Logger.debug(
            `[FS-DB] Querying (persistent) from ${identifier(
              collectionName,
              key
            )}`
          );

          const q = await getDocs(
            query(collection(db, collectionName), ...where)
          );
          _.forEach(q.docs, (doc) => {
            FETCHED.push(identifier(collectionName, doc.id));
          });
          Logger.debug(
            `[FS-DB] ${identifier(collectionName, key)} (persistent) returned ${
              q.docs.length
            } docs`
          );
          return q.docs;
        }
      ),
    (c, w, key) => key
  ),
  (c, w, key) => key
);

export default class FirestoreDocument {
  constructor() {
    this.migrated = false;
  }

  get collection() {
    return collection(db, this.constructor.COLLECTION);
  }

  get document() {
    return this.id ? doc(db, this.constructor.COLLECTION, this.id) : null;
  }

  async delete() {
    if (this.document) {
      Logger.debug(
        `[FS-DB] Deleting resource: ${identifier(
          this.constructor.COLLECTION,
          this.id
        )}`
      );
      FirestoreDocument.resetCache();
      return deleteDoc(this.document);
    }
  }

  async fetch() {
    const result = await FetchFromCache(this.constructor.COLLECTION, this.id);
    Logger.debug(
      `[FS-DB] ${identifier(this.constructor.COLLECTION, this.id)}: ${
        result ? 1 : 0
      }; Total fetched: ${FETCHED.length}`
    );
    this.migrated = _.get(result, ["data", "migrated"], false);
    return result;
  }

  get changesMade() {
    return !_.isEqual(this.json, this.json_original);
  }

  static async queryForDocs(collectionName, where = [], key) {
    const result = await QueryFromCache(collectionName, where, key);
    Logger.debug(
      `[FS-DB] ${identifier(collectionName, key)}: ${
        result.length
      }; Total fetched: ${FETCHED.length}`
    );
    return result;
  }

  static async queryForDocsPersistent(collectionName, where = [], key) {
    const result = await PersistentQueryFromCache(collectionName, where, key);
    Logger.debug(
      `[FS-DB] ${identifier(collectionName, key)} (persistent): ${
        result.length
      }; Total fetched: ${FETCHED.length}`
    );
    return result;
  }

  static resetCache() {
    Logger.debug("[FS-DB] Cache has been reset");
    FetchFromCache.cache.clear();
    QueryFromCache.cache.clear();
  }

  async save(preserveExistingDoc = true) {
    // Only save if changes were explicitly made OR this is a new (imported) doc
    if (!this.changesMade && this.id) {
      return Promise.resolve();
    }
    if (
      !CurrentSession.isSignedIn ||
      !CurrentSession.authenticatedUser?.uid ||
      !CurrentSession.authenticationStrictlyMatches(
        CurrentSession.authenticatedUser.uid
      ) ||
      (!!this.uid && this.uid !== CurrentSession.authenticatedUser.uid)
    ) {
      throw new Error(
        "You don't have permission to save this change. Are you signed in?"
      );
    }

    Logger.debug(`[FS-DB] Saving document for ${this.constructor.COLLECTION}`);
    FirestoreDocument.resetCache();

    const payload = { ...this.json, migrated: false };

    if (this.document) {
      return setDoc(this.document, payload, {
        merge: preserveExistingDoc,
      }).then(() => {
        this.migrated = false;
        this.saveCheckpoint();
      });
    } else {
      this.uid = CurrentSession.authenticatedUser.uid;
      payload.uid = this.uid;
      return addDoc(this.collection, payload).then((docRef) => {
        this.id = docRef.id;
        this.migrated = false;
        this.saveCheckpoint();
      });
    }
  }

  saveCheckpoint() {
    this.json_original = _.cloneDeep(this.json);
  }

  watch(callback) {
    if (!this.document) {
      Logger.error(`Cannot watch document: no document reference available`);
      return null;
    }

    return onSnapshot(
      this.document,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          callback(data);
        } else {
          Logger.debug(
            `No document found in ${this.constructor.COLLECTION}/${this.id}!`
          );
        }
      },
      (error) => {
        Logger.error(
          `Listen failed for ${this.constructor.COLLECTION}/${this.id}:`,
          error
        );
      }
    );
  }
}
